import { useState, type FormEvent } from 'react';
import { useLocation, useNavigate, type Location } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { loginApi, LoginError } from '@/api/auth';
import { AUTH_BASE_URL, configForPath, landingPath } from '@/features/dashboard/config';
import { getAuth, setAuth } from '@/features/auth/authStore';
import { TextInput } from '@/components/ui/TextInput';
import { Button } from '@/components/ui/Button';
import { Alert } from '@/components/ui/Alert';
import styles from '@/features/auth/LoginPage.module.css';

export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const navState = location.state as { from?: Location } | null;
  const requestedFrom = navState?.from?.pathname;

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const res = await loginApi(AUTH_BASE_URL, email, password);
      setAuth({ token: res.access_token, clientId: res.client_id, role: res.role });
      queryClient.clear();

      const allowed = getAuth()?.allowedReports ?? [];
      const isAdmin = res.role === 'admin';
      // The report the user asked for, when they may open it (admins: any);
      // otherwise the first report of their switcher.
      const fromCfg = requestedFrom ? configForPath(requestedFrom) : null;
      const fromIsAllowed = fromCfg ? isAdmin || allowed.includes(fromCfg.reportId) : false;
      const target = (fromIsAllowed ? requestedFrom : null) ?? landingPath(allowed, isAdmin);
      navigate(target, { replace: true });
    } catch (err) {
      setError(err instanceof LoginError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className={styles.page}>
      <form className={styles.card} onSubmit={onSubmit} noValidate>
        <div className={styles.lockup}>
          <div className={styles.chip} aria-hidden>
            C
          </div>
          <div>
            <div className={styles.title}>Client Portal</div>
            <div className={styles.subtitle}>Sign in to continue</div>
          </div>
        </div>

        <TextInput
          label="Email"
          type="email"
          autoComplete="username"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={submitting}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- single-purpose login form
          autoFocus
        />

        <TextInput
          label="Password"
          type="password"
          autoComplete="current-password"
          required
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          disabled={submitting}
        />

        {error && <Alert variant="error">{error}</Alert>}

        <Button type="submit" variant="primary" loading={submitting} loadingText="Signing in…" disabled={!email || !password}>
          Sign in
        </Button>
      </form>
    </div>
  );
}
